import { useCallback, useId } from 'react';

import PropTypes from 'prop-types';

import useToggledState from '@zenbusiness/application-commons-hooks/useToggledState';
import { useMessages } from '@zenbusiness/application-commons-intl';
import CardHeader from '@zenbusiness/zui/CardHeader';
import SlideSheet from '@zenbusiness/zui/SlideSheet';
import { zuiPropTypes } from '@zenbusiness/zui/utils/propTypes';
import { useSlotProps } from '@zenbusiness/zui/utils/useSlotProps';
import { useStableSlots } from '@zenbusiness/zui/utils/useStableSlots';

import { DEFAULT_SECTION_KEYS, SECTION_KEYS } from './constants';
import ExtraInfoCardContent from './Content';
import {
  ExtraInfoCardRoot as Card,
  ExtraInfoCardContent as CardContent,
  ExtraInfoCardButton as Button
} from './ExtraInfoCard.style';
import ExtraInfoCardListItem from './ListItem';
import ExtraInfoCardSection from './Section';

/**
 * Available message keys:
 *
 * - extraInfo.overline
 * - extraInfo.title
 * - extraInfo.subtitle
 * - extraInfoSheet.overline
 * - extraInfoSheet.title
 * - extraInfoSheet.subtitle
 * - extraInfoSheet.section.heading
 * - extraInfoSheet.section.body
 * - extraInfoSheet.section.1.heading
 * - extraInfoSheet.section.1.body
 */

function ExtraInfoCard({
  children,
  messageIds,
  messageKey,
  messageValues,
  primaryAction,
  sections = DEFAULT_SECTION_KEYS,
  slotProps: inSlotProps,
  ...delegated
}) {
  const { state: open, on: openSheet, off: closeSheet } = useToggledState(false);

  const MESSAGE_IDS = {
    overline: `${messageKey}.extraInfo.overline`,
    primaryAction: `shopping.component.extraInfo.primaryAction.label`,
    slideSheet: {
      overline: `${messageKey}.extraInfoSheet.overline`,
      subtitle: `${messageKey}.extraInfoSheet.subtitle`,
      title: `${messageKey}.extraInfoSheet.title`
    },
    subtitle: `${messageKey}.extraInfo.subtitle`,
    title: `${messageKey}.extraInfo.title`,
    ...messageIds
  };

  const messages = useMessages(MESSAGE_IDS, messageValues);

  const { slotProps } = useStableSlots(null, inSlotProps);
  const slideSheetProps = useSlotProps(SlideSheet, slotProps.slideSheet, {
    propsAdded: {
      onClose: closeSheet,
      open,
      overline: messages.slideSheet.overline,
      subtitle: messages.slideSheet.subtitle,
      title: messages.slideSheet.title
    }
  });

  const uniqueId = useId();
  const cardId = delegated.id ?? uniqueId;

  const labelledby = messages.title
    ? `${cardId}-extrainfo-title`
    : `${cardId}-extrainfo-definition`;
  const describedby = `${cardId}-extrainfo-definition`;

  const handleClick = useCallback(
    (e) => {
      openSheet();
      primaryAction?.onClick?.(e);
    },
    [openSheet, primaryAction]
  );

  return (
    <Card {...delegated}>
      <CardContent>
        <CardHeader
          overline={messages.overline}
          slotProps={{
            subtitle: { id: describedby, size: 'xs' },
            title: { id: labelledby, size: 'sm' }
          }}
          subtitle={messages.subtitle}
          title={messages.title}
        />
        <Button
          clickstreamProps={{
            elementName: 'extra_info_card_primary_action'
          }}
          data-testid="extra-info-card-primary-action"
          {...primaryAction}
          onClick={handleClick /* handleOpen */}
        >
          {messages.primaryAction}
        </Button>
      </CardContent>

      <SlideSheet {...slideSheetProps}>
        {children ?? (
          <ExtraInfoCardContent
            messageKey={messageKey}
            messageValues={messageValues}
            sections={sections}
          />
        )}
      </SlideSheet>
    </Card>
  );
}

ExtraInfoCard.propTypes = {
  children: PropTypes.node,
  messageIds: PropTypes.object,
  messageKey: PropTypes.string.isRequired,
  messageValues: PropTypes.object,
  primaryAction: PropTypes.shape({
    clickstreamProps: PropTypes.object,
    onClick: PropTypes.func
  }),
  sections: PropTypes.arrayOf(PropTypes.oneOf(Object.values(SECTION_KEYS))),
  slotProps: PropTypes.shape({
    slideSheet: zuiPropTypes.slotPropType
  })
};

ExtraInfoCard.ListItem = ExtraInfoCardListItem;
ExtraInfoCard.Section = ExtraInfoCardSection;
ExtraInfoCard.Content = ExtraInfoCardContent;

export default ExtraInfoCard;
